import mixpanel, { Mixpanel } from "mixpanel-browser"

import { ErrorReport, setErrorSink } from "lib/errorReporter"
import logger from "lib/logger"

let eventTracker: Mixpanel | null = null

// mixpanel 上でのイベント名。ダッシュボードのアラートはこの名前で張る
export const ERROR_EVENT_NAME = "FrontEnd Error"

// 初期化に失敗したことを残す。**ここは throw しない**（#427）。
//
// `logger` に **Error を渡す**のが要点（#426 のレビュー指摘）。Error を渡さないと
// 情報ログ扱いになり、本番では `console.log` 分岐も通らないので
// **console に 1 行も出ないまま**終わる（実測済み）。Error を渡せば、
// sink 未登録の分岐で `errorReporter.reportError` が `console.error` に落とす。
const reportInitFailure = (error: Error): void => {
  logger("initializeEventTracker FAILED", error)
}

// **この関数は決して throw しません（#427）。**
//
// `src/index.tsx` が **モジュールのトップレベル**で呼ぶので、ここで投げると
// `ReactDOM.render` に到達せず **アプリが 1 画面も描画されません（白画面）**。
// マウント前なのでエラーバウンダリも効きません。
//
// 計測を諦めてアプリは動かす。**ただし黙って諦めない。**
// トークン未設定だけでなく `mixpanel.init` 自体が投げた場合も同じ扱いにするので、
// 「計測基盤の都合でアプリが起動しない」形がここから無くなります。
export const initializeEventTracker = (): void => {
  const mixpanelToken = process.env.REACT_APP_MIXPANEL_TOKEN

  if (!mixpanelToken) {
    reportInitFailure(new Error("REACT_APP_MIXPANEL_TOKEN is not set"))

    return
  }

  try {
    eventTracker = mixpanel.init(
      mixpanelToken,
      {
        ignore_dnt: true,
      },
      "account",
    )
  } catch (error) {
    // 半端に初期化された状態を残さない。sink も張らない
    eventTracker = null
    setErrorSink(null)
    reportInitFailure(error instanceof Error ? error : new Error(String(error)))

    return
  }

  // logger が本番で送る先をここで配線する（#422）。init のあとに登録するので、
  // 送信時点では必ず初期化済み
  setErrorSink(trackError)

  logger("event tracker initialized")

  // set alias for the user from LP
  const mxid = new URLSearchParams(location.search).get("mxid")
  if (mxid) setTrackerAlias(mxid)
}

// 初期化できていないのに計測が呼ばれたときの共通処理。**投げません（#427）。**
//
// 呼び出し元はログイン処理（`LoginCallback`）とページの `useEffect`
// （`Login` / `SignUp` / `Team`）です。
//
// ★ 射程を正確に書くと（#431 の独立レビュー）:
//   - **ログインが実際に止まるのは `LoginCallback.tsx:85` の
//     `trackEvent({ eventName: "Sign Up" })`**（同期で `afterGetCallbackAction` の途中）
//   - `Login` / `SignUp` / `Team` の `useEffect` は、投げると
//     `ApplicationErrorBoundary` が発火して**画面が消えます**
//   - `LoginCallback.tsx:87` の `trackLoginEvent()` 経由の 3 つ
//     （`setTrackerAlias` / `trackEvent` / `setTrackingUserProfile`）は
//     `await loadProfile(...)` の後ろで await も `.catch` も無いので、
//     投げても unhandled rejection になるだけで `window.location.href` は実行されます
//
//   いずれにせよ計測のために画面を壊す理由が無いので、全部投げません。
// 黙って諦めないために、Error 付きで `logger` に流します。
//
// 再帰しません: sink が張られているのは初期化に成功したときだけで、
// そのとき `eventTracker` は null になりません。
const skipUninitialized = (functionName: string): void => {
  logger(
    `eventTracker: ${functionName} was called before initialization`,
    new Error("The event tracker in not initialized"),
  )
}

export const setTrackerAlias = (newId: string, existingId?: string): void => {
  if (!eventTracker) return skipUninitialized("setTrackerAlias")

  if (existingId) {
    eventTracker.alias(newId, existingId)
  } else {
    eventTracker.alias(newId)
  }

  logger("The alias is set to new id")
}

export const trackEvent = (
  event: { eventName: string },
  params?: Record<string, unknown>,
): void => {
  if (!eventTracker) return skipUninitialized("trackEvent")

  if (params) {
    params.service = "ACCOUNT"
    params.origin = "FrontEnd"
  } else {
    params = { service: "ACCOUNT", origin: "FrontEnd" }
  }
  eventTracker.track(event.eventName, params)
}

export const setTrackingUserProfile = async (
  profile: Record<string, unknown>,
): Promise<void> => {
  if (!eventTracker) return skipUninitialized("setTrackingUserProfile")

  eventTracker.people.set(profile)
}

// logger から呼ばれるエラー送信（#422）。**`trackEvent` と同じ mixpanel インスタンス**
// （プロジェクト名 "account"）に送る。
//
// **ここだけは初期化前に呼ばれたら例外を投げます。** 上の 3 つ（`trackEvent` など）と
// 方針が違うのは、**呼び出し元が違う**からです:
//   - `trackEvent` などの呼び出し元は UI（ログイン処理・`useEffect`）。投げると画面が壊れる
//   - `trackError` の呼び出し元は `errorReporter.reportError` **だけ**で、
//     そこは投げられることを前提に `try`/`catch` で受け、`console.error` に落とす
// つまりここで投げることが「送れなかった事実を残す」手段になっています。
// 黙って return に変えると、その事実が消えます（#426 の spec が固定済み）。
export const trackError = ({ tag, error, context }: ErrorReport): void => {
  if (!eventTracker) throw new Error("The event tracker in not initialized")

  eventTracker.track(ERROR_EVENT_NAME, {
    service: "ACCOUNT",
    origin: "FrontEnd",
    tag: tag,
    errorName: error.name,
    errorMessage: error.message,
    errorStack: error.stack,
    context: context,
  })
}
